import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons'
import {
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Skeleton,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
  type BoxProps,
} from '@mantine/core'
import dayjs from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { useTranslation } from '@flecto/translations'
import { Icon, InputPhoneNumber } from '@flecto/ui'

import { useStoreBasket } from '../../hooks/useStoreBasket'
import { useStoreDates } from '../../hooks/useStoreDates'
import { useStoreDetails } from '../../hooks/useStoreDetails'
import { useStorePathBuilder } from '../../hooks/useStorePathBuilder'
import { RowProduct } from '../DropdownBasket/RowProduct'
import { useFormContext } from '../FormCheckout/FormCheckout'
import { useFormCheckoutState } from '../FormCheckout/useFormCheckoutState'
import { SectionDatesSelector } from '../SectionDatesSelector/SectionDatesSelector'
import { TextCurrencyStore } from '../TextCurrencyStore/TextCurrencyStore'
import styles from './styles.module.css'

dayjs.extend(isToday)

export type SectionCheckoutProps = BoxProps

export function SectionCheckout({ ...props }: SectionCheckoutProps) {
  const { t } = useTranslation('store', { keyPrefix: 'store-page-checkout' })
  const router = useRouter()
  const path = useStorePathBuilder()
  const form = useFormContext()
  const store = useStoreDetails()

  const { isLoading: isFormLoading } = useFormCheckoutState()

  const { products, isLoadingTotal, totalValue, recalculateTotalValue } =
    useStoreBasket()

  const { startAt, endAt, setStartAt, setEndAt } = useStoreDates()

  useEffect(() => {
    recalculateTotalValue()
  }, [])

  useEffect(() => {
    recalculateTotalValue()
  }, [startAt, endAt])

  useEffect(() => {
    if (products.length <= 0) {
      router.push(path('/'))
    }
  }, [products])

  return (
    <Box {...props} className={styles.wrapper}>
      <Box>
        <Card mb='md' className={styles.dates}>
          <Title order={5} mb='md'>
            {t('card_rental_dates.title')}
            <Text c='red' component='span' ml='xs'>
              *
            </Text>
          </Title>
          <SectionDatesSelector
            isInline
            startAt={form.values.start_at}
            endAt={form.values.end_at}
            errorStartAt={form.errors.start_at}
            errorEndAt={form.errors.end_at}
            onChangeStartAt={(date) => {
              form.setFieldValue('start_at', date)
              setStartAt(date)
            }}
            onChangeEndAt={(date) => {
              setEndAt(date)
              form.setFieldValue('end_at', date)
            }}
          />
          <Flex gap='md' mt='lg' mb='xs' align='center'>
            <Text fw={500}>{t('card_rental_dates.label_flexible_dates')}</Text>
            <Switch
              {...form.getInputProps('flexible_dates', { type: 'checkbox' })}
            />
          </Flex>
        </Card>
        <Card className={styles.information}>
          <Title order={5} mb='md'>
            {t('card_your_info.title')}
          </Title>
          <TextInput
            label={t('card_your_info.label_name')}
            placeholder='Ana Silva'
            withAsterisk
            {...form.getInputProps('full_name')}
          />
          <Flex
            gap='md'
            mt='lg'
            align='flex-start'
            className={styles.emailPhone}
          >
            <TextInput
              type='email'
              placeholder='mail@example.com'
              label={t('card_your_info.label_email')}
              w='100%'
              withAsterisk
              {...form.getInputProps('email')}
            />
            <InputPhoneNumber
              label={t('card_your_info.label_phone_number')}
              w='100%'
              {...form.getInputProps('_phone_number')}
              value={form.values._phone_number}
              onChange={(e) =>
                form.setFieldValue('_phone_number', e.target.value)
              }
              valueDialCode={form.values._phone_number_country_code}
              withAsterisk
              onChangeDialCode={(dialCode) => {
                form.setFieldValue('_phone_number_country_code', dialCode)
              }}
            />
          </Flex>
          <Textarea
            label={t('card_your_info.label_message_to_store', {
              store: store?.name,
            })}
            withAsterisk
            mt='lg'
            minRows={4}
            {...form.getInputProps('renter_message')}
          />
        </Card>
      </Box>
      <Card style={{ height: 'max-content' }}>
        <Title order={5} mb='md'>
          {t('card_summary.title')}
        </Title>
        <Flex direction='column' gap='lg'>
          {products.map((p, key) => (
            <RowProduct key={key} product={p.product} withBundleInfo />
          ))}
        </Flex>
        <Card.Section>
          <Divider color='gray.2' my='lg' />
        </Card.Section>
        <Flex align='center' justify='space-between' mb='md'>
          <Flex align='center' gap='xs'>
            <Text fw={500}>{t('card_summary.label_total')}</Text>
            <Tooltip
              label={t('card_summary.label_total_tooltip')}
              withinPortal={true}
            >
              <Box mt='xs'>
                <Icon icon={faQuestionCircle} mt={1} size='xs' color='gray.6' />
              </Box>
            </Tooltip>
          </Flex>
          {isLoadingTotal ? (
            <Skeleton h={15} w={100} />
          ) : startAt && endAt ? (
            <TextCurrencyStore cents={totalValue} />
          ) : (
            <Text size='sm'>{t('card_summary.label_select_dates_first')}</Text>
          )}
        </Flex>
        <Button
          fullWidth
          mt='lg'
          size='lg'
          type='submit'
          form='form-checkout'
          loading={isFormLoading}
        >
          {t('card_summary.label_button')}
        </Button>
        <Flex align='center' justify='center' mt='md' mb='xs'>
          <Text c='gray.5' size='xs' ta='center'>
            *{t('card_summary.label_disclaimer')}
          </Text>
        </Flex>
      </Card>
    </Box>
  )
}
